import {
  clonePreset,
  createBoleroDemoClip,
  createCelesteDemoClip,
  createDemoFxStack,
  createOdinisDemoClip,
  DEFAULT_PRESETS,
  ELECTRONIC_PRESETS,
  KEYGEN_DEMO_PATTERN,
  KEYGEN_PRESETS,
  MODULATION_PRESETS,
  CLASSICAL_PRESETS,
  normalizeFxNode,
  parsePatternString,
  type ClipIR,
  type FxNode,
  type Instrument,
  type SlotMix,
  type SynthPreset,
} from "../../../../src/lib/audio";
import chipGrooveProject from "../../assets/demos/chip-groove.project.json";
import type { NamedDrumKit } from "./drumKitBank";
import type { NamedPreset } from "./presetBank";
import { parseLabProject, type LabProjectSnapshot } from "./projectFile";

export type LabDemoId = "chip" | "keygen" | "odinis" | "bolero" | "celeste";

/** Payload returned by each demo loader (optional fields filled by loadDemo). */
export type LabDemoPayload = {
  clip: ClipIR;
  presets: SynthPreset[];
  fx: FxNode[];
  instruments?: Instrument[];
  bank?: NamedPreset[];
  drumBank?: NamedDrumKit[];
  slotMix?: SlotMix[];
  slotFx?: FxNode[][];
  muted?: boolean[];
  solo?: boolean[];
};

export type LabDemo = {
  id: LabDemoId;
  /** Short toolbar label. */
  label: string;
  /** Status-bar blurb after load. */
  description: string;
  load: () => LabDemoPayload;
};

/** Authored Chip Groove project (pattern + instruments + per-slot/master FX). */
export function loadChipGrooveProject(): LabProjectSnapshot {
  return parseLabProject(chipGrooveProject);
}

function payloadFromProject(snapshot: LabProjectSnapshot): LabDemoPayload {
  return {
    clip: snapshot.clip,
    presets: snapshot.presets,
    fx: snapshot.fx,
    instruments: snapshot.instruments,
    bank: snapshot.bank,
    drumBank: snapshot.drumBank,
    slotMix: snapshot.slotMix,
    slotFx: snapshot.slotFx,
    muted: snapshot.muted,
    solo: snapshot.solo,
  };
}

function keygenPreset(name: string): SynthPreset {
  const preset = KEYGEN_PRESETS.find(entry => entry.name === name);
  if (!preset) {
    throw new Error(`Missing keygen preset: ${name}`);
  }
  return clonePreset(preset);
}

/** Resolve a factory voice across keygen / electronic / modulation / classical / defaults. */
function factoryPreset(name: string): SynthPreset {
  const preset =
    KEYGEN_PRESETS.find(entry => entry.name === name)
    ?? ELECTRONIC_PRESETS.find(entry => entry.name === name)
    ?? MODULATION_PRESETS.find(entry => entry.name === name)
    ?? CLASSICAL_PRESETS.find(entry => entry.name === name)
    ?? DEFAULT_PRESETS.find(entry => entry.name === name);
  if (!preset) {
    throw new Error(`Missing factory preset: ${name}`);
  }
  return clonePreset(preset);
}

/**
 * Odinis kick from the authored modern mix — factory Kick with a tighter body
 * (octave 0 / −7 semis, lower cutoff).
 */
const ODINIS_KICK: SynthPreset = {
  name: "Kick",
  osc1: { wave: "sine", octave: 0, semis: -7, detuneCents: 0, level: 1, pwm: 0.5 },
  osc2: { wave: "noise", octave: 0, semis: 0, detuneCents: 0, level: 0.12, pwm: 0.5 },
  filter: { type: "lowpass", cutoffHz: 535, resonance: 0.2 },
  ampEnv: { a: 0.001, d: 0.22, s: 0, r: 0.06 },
  modEnv: { a: 0.001, d: 0.08, s: 0, r: 0.01 },
  lfo: { rateHz: 0, wave: "sine", depth: 0 },
  routes: [
    { source: "modEnv", target: "pitch", depth: 24 },
    { source: "modEnv", target: "cutoff", depth: 2 },
  ],
};

function createKeygenDemoClip(): ClipIR {
  const { clip, warnings } = parsePatternString(KEYGEN_DEMO_PATTERN);
  if (warnings.length > 0) {
    console.warn("Keygen demo parse warnings:", warnings);
  }
  return clip;
}

/** Soft keygen FX — a touch more room than a dry mix. */
function createKeygenDemoFxStack(): FxNode[] {
  return createDemoFxStack().map(node => {
    if (node.type === "reverb") {
      return { ...node, wet: 0.32, params: { ...node.params, room: 0.5, damp: 0.48 } };
    }
    if (node.type === "chorus") {
      return { ...node, wet: 0.18, params: { ...node.params, rateHz: 0.55, depthMs: 3.2 } };
    }
    return node;
  });
}

/**
 * Celeste cart stack — short slap delay + grit into soft chorus/reverb
 * (authored project mix).
 */
function createCelesteDemoFxStack(): FxNode[] {
  return [
    normalizeFxNode({
      type: "delay",
      wet: 0.17,
      params: { timeMs: 18, feedback: 0.35 },
    }),
    normalizeFxNode({
      type: "distortion",
      wet: 0.33,
      params: { drive: 0.22 },
    }),
    normalizeFxNode({
      type: "chorus",
      wet: 0.18,
      params: { rateHz: 0.55, depthMs: 3.2, delayMs: 7 },
    }),
    normalizeFxNode({
      type: "reverb",
      wet: 0.32,
      params: { room: 0.26, damp: 0.4 },
    }),
  ];
}

/**
 * Boléro tambour from the authored mix — Cavern Tom with a noisier, brighter
 * body so the ostinato reads as snare rather than a soft kick.
 */
const BOLERO_TAMBOUR: SynthPreset = {
  name: "Cavern Tom — tambour",
  osc1: { wave: "tri", octave: -1, semis: -2, detuneCents: 0, level: 0.72, pwm: 0.5 },
  osc2: { wave: "noise", octave: 0, semis: 0, detuneCents: 0, level: 0.61, pwm: 0.5 },
  filter: { type: "lowpass", cutoffHz: 1398, resonance: 0.25 },
  ampEnv: { a: 0.001, d: 0.42, s: 0, r: 0.12 },
  modEnv: { a: 0.001, d: 0.16, s: 0, r: 0.03 },
  lfo: { rateHz: 0, wave: "sine", depth: 0 },
  routes: [
    { source: "modEnv", target: "pitch", depth: 14 },
    { source: "modEnv", target: "cutoff", depth: 1.6 },
  ],
};

/** Authored Boléro mix — light grit + slap delay into soft chorus/hall. */
function createBoleroDemoFxStack(): FxNode[] {
  return [
    normalizeFxNode({
      type: "distortion",
      wet: 0.24,
      params: { drive: 6 },
    }),
    normalizeFxNode({
      type: "delay",
      wet: 0.21,
      params: { timeMs: 120, feedback: 0.51 },
    }),
    normalizeFxNode({
      type: "chorus",
      wet: 0.1,
      params: { rateHz: 0.35, depthMs: 2.2, delayMs: 7 },
    }),
    normalizeFxNode({
      type: "reverb",
      wet: 0.38,
      params: { room: 0.68, damp: 0.4 },
    }),
  ];
}

/** Modern Odinis mix — delay + grit into chorus/reverb (bitcrush parked off). */
function createOdinisDemoFxStack(): FxNode[] {
  return [
    normalizeFxNode({
      type: "bitcrush",
      enabled: false,
      wet: 0.26,
      params: { bits: 6, downsample: 4 },
    }),
    normalizeFxNode({
      type: "delay",
      wet: 0.19,
      params: { timeMs: 133, feedback: 0.35 },
    }),
    normalizeFxNode({
      type: "distortion",
      wet: 0.3,
      params: { drive: 6.5 },
    }),
    normalizeFxNode({
      type: "chorus",
      wet: 0.23,
      params: { rateHz: 0.7, depthMs: 2.8, delayMs: 7 },
    }),
    normalizeFxNode({
      type: "reverb",
      wet: 0.23,
      params: { room: 0.45, damp: 0.5 },
    }),
  ];
}

export const LAB_DEMOS: readonly LabDemo[] = [
  {
    id: "chip",
    label: "Chip groove",
    description: "64-step chip groove · Mono PWM Bass/Basement Organ · slot+master FX",
    load: () => payloadFromProject(loadChipGrooveProject()),
  },
  {
    id: "keygen",
    label: "Keygen loop",
    description: "512-step keygen · 8 voices · Cm · soft chorus/reverb",
    load: () => ({
      clip: createKeygenDemoClip(),
      presets: [
        keygenPreset("Warm Pluck Bass"),
        keygenPreset("Airy Keys"),
        keygenPreset("Soft Kick"),
        keygenPreset("Tick Hat"),
        keygenPreset("Nostalgia Pad"),
        keygenPreset("Chirp Arp"),
        keygenPreset("Paper Snare"),
        keygenPreset("Glass Bell"),
      ],
      fx: createKeygenDemoFxStack(),
    }),
  },
  {
    id: "odinis",
    label: "Odinis theme",
    description: "1024-step · pluck/clarion/cello · Em progression · delay+grit FX",
    load: () => {
      // Chord tracks share Amber Cello so the triad rings as one instrument.
      const cello = factoryPreset("Amber Cello");
      return {
        clip: createOdinisDemoClip(),
        presets: [
          factoryPreset("Neon Rain Pluck"),
          factoryPreset("Gilded Clarion"),
          clonePreset(cello),
          clonePreset(cello),
          clonePreset(cello),
          clonePreset(ODINIS_KICK),
          factoryPreset("Hat"),
          factoryPreset("Tick Hat"),
        ],
        fx: createOdinisDemoFxStack(),
      };
    },
  },
  {
    id: "bolero",
    label: "Boléro",
    description: "6120-step Ravel Boléro · 340 bars · winds/fiddle/cello/tambour · grit+hall FX",
    load: () => {
      const fiddle = factoryPreset("Baroque Fiddle");
      const hautbois = factoryPreset("Nasal Hautbois");
      hautbois.osc1 = { ...hautbois.osc1, level: 0.43, pwm: hautbois.osc1.pwm ?? 0.28 };
      hautbois.osc2 = { ...hautbois.osc2, level: 0.12, pwm: hautbois.osc2.pwm ?? 0.5 };
      return {
        clip: createBoleroDemoClip(),
        presets: [
          factoryPreset("Silver Traverso"),
          hautbois,
          factoryPreset("Gilded Clarion"),
          factoryPreset("Ring Spire"),
          clonePreset(fiddle),
          clonePreset(fiddle),
          factoryPreset("Amber Cello"),
          clonePreset(BOLERO_TAMBOUR),
        ],
        fx: createBoleroDemoFxStack(),
      };
    },
  },
  {
    id: "celeste",
    label: "Celeste",
    description: "5504-step Celeste Classic medley · Title→Summit · iceberg/choir/chip · slap+grit",
    load: () => {
      const lead = factoryPreset("Iceberg Lead");
      lead.glideMs = 9;
      return {
        clip: createCelesteDemoClip(),
        presets: [
          lead,
          factoryPreset("Chirp Arp"),
          factoryPreset("Tracker Choir"),
          factoryPreset("Glass Bell"),
          factoryPreset("Tick Hat"),
          factoryPreset("Paper Snare"),
          factoryPreset("Tick Hat"),
          factoryPreset("Paper Snare"),
        ],
        fx: createCelesteDemoFxStack(),
      };
    },
  },
];

export function getLabDemo(id: string): LabDemo | undefined {
  return LAB_DEMOS.find(demo => demo.id === id);
}
